import { FC, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { CheckOutlined, LoadingOutlined } from '@ant-design/icons'
import FieldWrapper from '@pages/Admin/CourseSettings/components/FieldWrapper/FieldWrapper'
import TooltipWrapper from '@pages/Admin/CourseSettings/components/TooltipWrapper/TooltipWrapper'
import { useAddAlert } from '@/hooks/useAddAlert'
import { updateUserNotificationChannel } from '../../utils/api'
import { NOTIFICATION_CHANNEL_OPTIONS } from '../../utils/mapper'
import {
  NotificationChannel,
  NotificationSettingsItem,
} from '../../utils/types'
import cn from './index.module.css'

interface INotificationSettingsCard {
  userId: number
  notification: NotificationSettingsItem
  onChange?: (notification: NotificationSettingsItem) => void
}

const NotificationSettingsCard: FC<INotificationSettingsCard> = ({
  userId,
  notification,
  onChange,
}) => {
  const addAlert = useAddAlert()
  const [activeChannels, setActiveChannels] = useState<NotificationChannel[]>(
    notification.active_channels ?? [],
  )
  const [processingChannel, setProcessingChannel] =
    useState<NotificationChannel | null>(null)

  const channels = useMemo(() => {
    const source = notification.all_channels?.length
      ? notification.all_channels
      : activeChannels

    return Array.from(new Set([...source, ...activeChannels]))
  }, [activeChannels, notification.all_channels])

  const handleToggleChannel = async (channel: NotificationChannel) => {
    if (processingChannel) return

    const prevChannels = activeChannels
    const isActive = prevChannels.includes(channel)
    const nextChannels = isActive
      ? prevChannels.filter((item) => item !== channel)
      : [...prevChannels, channel]

    setActiveChannels(nextChannels)
    setProcessingChannel(channel)

    const response = await updateUserNotificationChannel(
      userId,
      notification.id,
      channel,
      !isActive,
    )

    setProcessingChannel(null)

    if (!response.success) {
      setActiveChannels(prevChannels)
      addAlert({
        title: 'Ошибка',
        text: response.error || 'Не удалось обновить канал уведомления',
        type: 'error',
      })
      return
    }

    onChange?.({ ...notification, active_channels: nextChannels })
    addAlert({
      title: isActive ? 'Канал отключен' : 'Канал включен',
    })
  }

  useEffect(() => {
    setActiveChannels(notification.active_channels ?? [])
  }, [notification.active_channels])

  return (
    <div className={cn.card}>
      <div className={cn.content}>
        <div className={cn.title}>{notification.title}</div>
        <div className={cn.description}>{notification.description}</div>
        <FieldWrapper label="Каналы уведомления" className={cn.channelsWrapper}>
          <div className={cn.tagList}>
            {channels.map((channel) => {
              const isActive = activeChannels.includes(channel)
              const isProcessing = processingChannel === channel

              return (
                <TooltipWrapper
                  key={channel}
                  title={
                    isProcessing
                      ? 'Сохраняется'
                      : isActive
                      ? 'Нажмите, чтобы отключить'
                      : 'Нажмите, чтобы включить'
                  }
                >
                  <button
                    type="button"
                    disabled={!!processingChannel}
                    className={classNames(
                      cn.tag,
                      isActive && cn.completedTag,
                      isProcessing && cn.processingTag,
                    )}
                    onClick={() => handleToggleChannel(channel)}
                  >
                    <span className={cn.tagText}>
                      {NOTIFICATION_CHANNEL_OPTIONS[channel]?.label ?? channel}
                    </span>
                    {isActive && !isProcessing && (
                      <CheckOutlined
                        className={cn.checkIcon}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                      />
                    )}
                    {isProcessing && (
                      <LoadingOutlined
                        className={cn.processingIcon}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                      />
                    )}
                  </button>
                </TooltipWrapper>
              )
            })}
          </div>
        </FieldWrapper>
      </div>
    </div>
  )
}

export default NotificationSettingsCard
